/**
 * VixoKart Theme — Recently Viewed Products
 * Stores last N product handles in localStorage and renders a grid.
 * Works entirely client-side with the storefront API.
 */

'use strict';

(function initRecentlyViewed() {
  const STORAGE_KEY = 'vixokart_recently_viewed';
  const MAX_ITEMS   = 8;

  // ─── Storage helpers ─────────────────────────────────────────────────────────

  function getViewed() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
    catch { return []; }
  }

  function saveViewed(list) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); }
    catch {} // silently fail if storage is full
  }

  // ─── Track current product ────────────────────────────────────────────────────

  const currentHandle = document.querySelector('[data-product-handle]')?.dataset.productHandle;
  if (currentHandle) {
    const viewed = getViewed().filter(h => h !== currentHandle);
    viewed.unshift(currentHandle);
    saveViewed(viewed.slice(0, MAX_ITEMS));
  }

  // ─── Render section ───────────────────────────────────────────────────────────

  const section = document.querySelector('.recently-viewed-section');
  if (!section) return;

  const grid = section.querySelector('.recently-viewed-grid');
  if (!grid) return;

  const viewed = getViewed().filter(h => h !== currentHandle);
  if (viewed.length === 0) {
    section.style.display = 'none';
    return;
  }

  // Fetch product data from Storefront JSON
  const limit = Math.min(viewed.length, 6);

  Promise.all(
    viewed.slice(0, limit).map(handle =>
      fetch(`/products/${handle}.js`)
        .then(r => r.json())
        .catch(() => null)
    )
  ).then(products => {
    const valid = products.filter(Boolean);
    if (valid.length === 0) {
      section.style.display = 'none';
      return;
    }

    grid.innerHTML = valid.map(product => {
      const price       = VixoKart.formatMoney(product.price_min);
      const compareAt   = product.compare_at_price_min;
      const hasDiscount = compareAt && compareAt > product.price_min;
      const discount    = hasDiscount
        ? Math.round((1 - product.price_min / compareAt) * 100)
        : 0;
      const imgSrc = product.featured_image
        ? product.featured_image + '&width=400'
        : '';
      const inStock = product.available;

      return `
        <div class="product-card reveal">
          <div class="product-card__image-wrap ratio-portrait">
            <a class="product-card__link" href="/products/${product.handle}" aria-label="${product.title}">
              ${imgSrc ? `<img class="product-card__img" src="${imgSrc}" alt="${product.title}" loading="lazy" width="400" height="533">` : ''}
            </a>
              <div class="product-card__badges">
                ${hasDiscount ? `<span class="badge badge--sale">−${discount}%</span>` : ''}
              </div>
              ${!inStock ? `
                <button
                  class="product-card__wishlist"
                  data-wishlist
                  data-product-id="${product.id}"
                  data-product-title="${product.title}"
                  data-product-url="/products/${product.handle}"
                  data-product-image="${imgSrc}"
                  data-product-price="${price}"
                  aria-label="Add to wishlist"
                  type="button"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                </button>` : ''}
              ${inStock && product.variants?.[0] ? `
                <button
                  class="product-card__plus-add"
                  data-quick-add
                  data-variant-id="${product.variants[0].id}"
                  aria-label="Add ${product.title} to cart"
                  type="button"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <line x1="8" y1="2" x2="8" y2="14" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
                    <line x1="2" y1="8" x2="14" y2="8" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
                  </svg>
                </button>` : ''}
          </div>
          <div class="product-card__body">
            <a class="product-card__title" href="/products/${product.handle}">${product.title}</a>
            <div class="product-card__price">
              <span class="product-card__price-current">${price}</span>
              ${hasDiscount ? `<span class="product-card__price-compare">${VixoKart.formatMoney(compareAt)}</span>` : ''}
            </div>
          </div>
        </div>
      `;
    }).join('');

    // Re-observe for animations
    grid.querySelectorAll('.reveal').forEach(el => VixoKart.observeReveal?.(el));

  }).catch(() => {
    section.style.display = 'none';
  });

})();
